import { isSameYear, isSameMonth, getDate, isLastDayOfMonth, getYear, isSameDay, format, getMonth, getQuarter, isYesterday, isToday, isThisWeek, isThisYear, parse, differenceInCalendarDays, isTomorrow, parseISO, isPast, } from 'date-fns';
import getSymbolFromCurrency from 'currency-symbol-map';
import flag from 'country-code-emoji';
import { padStart } from 'lodash';
import { Tier } from 'features/premium/entities';
export const formatDate = (date) => {
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) {
        return `Today at ${format(parsedDate, 'h:mm a')}`;
    }
    if (isYesterday(parsedDate)) {
        return `Yesterday at ${format(parsedDate, 'h:mm a')}`;
    }
    if (isThisWeek(parsedDate)) {
        return `${format(parsedDate, 'EEEE')} at ${format(parsedDate, 'h:mm a')}`;
    }
    return format(parsedDate, "MMM do 'at' h:mm a");
};
export const formatETADateForItem = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (isToday(parsedDate)) {
        return 'Arrives today';
    }
    if (isTomorrow(parsedDate)) {
        return 'Arrives tomorrow';
    }
    if (isThisYear(parsedDate)) {
        return `Arrives on ${format(parsedDate, 'd MMM')}`;
    }
    return `Arrives on ${format(parsedDate, 'd MMM yyyy')}`;
};
// "15 Apr", or "15 Apr 2025" when the year differs from the current one.
export const formatDayMonth = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    return format(parsedDate, isThisYear(parsedDate) ? 'd MMM' : 'd MMM yyyy');
};
export const formatDateForItem = (date, prefix, lowercase) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    const applyCase = (word) => (lowercase ? word.toLowerCase() : word);
    if (isToday(parsedDate)) {
        return prefix ? `${prefix} ${applyCase('Today')}` : applyCase('Today');
    }
    if (isYesterday(parsedDate)) {
        return prefix
            ? `${prefix} ${applyCase('Yesterday')}`
            : applyCase('Yesterday');
    }
    if (isTomorrow(parsedDate)) {
        return prefix
            ? `${prefix} ${applyCase('Tomorrow')}`
            : applyCase('Tomorrow');
    }
    if (isThisWeek(parsedDate) && isPast(parsedDate)) {
        return prefix
            ? `${prefix} on ${format(parsedDate, "'last' EEEE")}`
            : format(parsedDate, `'${applyCase('Last')}' EEEE`);
    }
    if (isThisYear(parsedDate)) {
        return prefix
            ? `${prefix} on ${format(parsedDate, 'd MMM')}`
            : format(parsedDate, 'd MMM');
    }
    return prefix
        ? `${prefix} on ${format(parsedDate, 'd MMM yyyy')}`
        : format(parsedDate, 'd MMM yyyy');
};
const PERIOD_FORMAT_NO_YEAR = 'd MMM';
const PERIOD_FORMAT_WITH_YEAR_SHORT = "d MMM ''yy";
const PERIOD_FORMAT_WITH_YEAR = 'd MMM yyyy';
const shortHyphenated = (from, to, withYear) => {
    if (withYear) {
        if (!isSameYear(from, to)) {
            return `${format(from, PERIOD_FORMAT_WITH_YEAR)} - ${format(to, PERIOD_FORMAT_WITH_YEAR)}`;
        }
        if (isSameMonth(from, to)) {
            return `${format(from, 'd')} - ${format(to, PERIOD_FORMAT_WITH_YEAR)}`;
        }
        return `${format(from, PERIOD_FORMAT_NO_YEAR)} - ${format(to, PERIOD_FORMAT_WITH_YEAR)}`;
    }
    if (isSameMonth(from, to)) {
        return `${format(from, 'd')} - ${format(to, 'd MMM')}`;
    }
    return `${format(from, 'd MMM')} - ${format(to, 'd MMM')}`;
};
const multiLine = (from, to, formatString) => `${format(from, formatString)}\n${format(to, formatString)}`;
const hyphenated = (from, to, formatString) => `${format(from, formatString)} - ${format(to, formatString)}`;
export const formatPeriodDate = (period, short, excludeYear, alwaysShowBoth, cropSameMonth, alwaysShowYear) => {
    const { from, to } = period;
    if (to &&
        isSameYear(from, to) &&
        isSameMonth(from, to) &&
        getDate(from) === 1 &&
        isLastDayOfMonth(to)) {
        return formatMonth(from, short, alwaysShowYear);
    }
    if (to && checkCompleteQuarter(from, to)) {
        return formatQuarter(from);
    }
    if (getDate(from) === 1 &&
        getMonth(from) === 0 &&
        to &&
        isLastDayOfMonth(to) &&
        getMonth(to) === 11 &&
        isSameYear(from, to)) {
        return format(from, 'yyyy');
    }
    const sameYear = getYear(from) === getYear(new Date());
    if (to && isSameDay(from, to) && !alwaysShowBoth) {
        if ((sameYear || excludeYear) && !alwaysShowYear) {
            return format(from, 'd MMM');
        }
        return format(from, short ? "d MMM ''yy" : 'd MMM yyyy');
    }
    if ((sameYear || excludeYear) && !alwaysShowYear) {
        if (!to) {
            return format(from, PERIOD_FORMAT_NO_YEAR);
        }
        if (cropSameMonth) {
            return shortHyphenated(from, to);
        }
        return short
            ? multiLine(from, to, PERIOD_FORMAT_NO_YEAR)
            : hyphenated(from, to, PERIOD_FORMAT_NO_YEAR);
    }
    if (!to) {
        return format(from, PERIOD_FORMAT_WITH_YEAR);
    }
    if (cropSameMonth) {
        return shortHyphenated(from, to, alwaysShowYear);
    }
    return short
        ? multiLine(from, to, PERIOD_FORMAT_WITH_YEAR_SHORT)
        : hyphenated(from, to, PERIOD_FORMAT_WITH_YEAR);
};
export const formatPeriod = (period, short, excludeYear, alwaysShowBoth, cropSameMonth, alwaysShowYear) => {
    const from = period.from ? parseISO(period.from) : new Date();
    const to = period.to ? parseISO(period.to) : undefined;
    return formatPeriodDate({ from, to }, short, excludeYear, alwaysShowBoth, cropSameMonth, alwaysShowYear);
};
export const formatMonth = (from, short, includeYear) => {
    const date = typeof from === 'string' ? parseISO(from) : from;
    if (isSameYear(date, new Date()) && !includeYear) {
        return format(date, short ? 'MMM' : 'MMMM');
    }
    return format(date, short ? "MMM ''yy" : 'MMMM yyyy');
};
export const formatQuarter = (from) => {
    const date = typeof from === 'string' ? parseISO(from) : from;
    const year = getYear(date);
    const quarter = getQuarter(date);
    return `Q${quarter} ${year}`;
};
const checkCompleteQuarter = (startDate, endDate) => {
    const startMonth = getMonth(startDate);
    const startDay = getDate(startDate);
    const endMonth = getMonth(endDate);
    const endDay = getDate(endDate);
    // Check for Q1: January 1st to March 31st
    const isQ1 = startMonth === 0 && startDay === 1 && endMonth === 2 && endDay === 31;
    // Check for Q2: April 1st to June 30th
    const isQ2 = startMonth === 3 && startDay === 1 && endMonth === 5 && endDay === 30;
    // Check for Q3: July 1st to September 30th
    const isQ3 = startMonth === 6 && startDay === 1 && endMonth === 8 && endDay === 30;
    // Check for Q4: October 1st to December 31st
    const isQ4 = startMonth === 9 && startDay === 1 && endMonth === 11 && endDay === 31;
    return ((isQ1 || isQ2 || isQ3 || isQ4) && getYear(startDate) === getYear(endDate));
};
export const formatRangeTextFromDates = (start, end, dateFormat = 'yyyy-MM-dd') => {
    const range = 'Any day of the month';
    if (!start || !end)
        return range;
    const startDate = typeof start === 'string'
        ? getDate(parse(start, dateFormat, new Date()))
        : getDate(start);
    const endDate = typeof end === 'string'
        ? getDate(parse(end, dateFormat, new Date()))
        : getDate(end);
    if (startDate >= 1 && endDate <= 15)
        return 'The start of the month';
    if (startDate > 15 && endDate <= 31)
        return 'End of the month';
    if (endDate - startDate > 20)
        return 'Any day of the month';
    if (startDate >= 10 && endDate <= 20)
        return 'Middle of the month';
    return `${format(typeof start === 'string' ? parseISO(start) : start, 'do')} to the ${format(typeof end === 'string' ? parseISO(end) : end, 'do')} of the month`;
};
const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
];
export const getCompactNumber = (num, digits = 1) => {
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    const item = lookup
        .slice()
        .reverse()
        .find((item) => num >= item.value);
    return item
        ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol
        : '0';
};
export const NON_BREAKING_CHAR = '\u2060';
export const formatAmount = (value, currency, fractionDigits, // null means variable fraction digits - as needed
scramble = false, // not in use right now
removeZero) => {
    if (value === undefined || value === null) {
        return '';
    }
    const newValue = scramble ? value * Math.random() * 10 : value;
    let fraction = fractionDigits ?? (Math.round(value) === value ? 0 : 2);
    // don’t try to use Number.toLocaleString - it won’t work in JSI on device
    let result;
    if (fractionDigits === null) {
        result = newValue.toFixed(getFractionDigits(newValue));
        const decimalsDelimeterPosition = result.indexOf('.');
        if (decimalsDelimeterPosition === -1) {
            fraction = 0;
        }
        else {
            fraction = result.length - 1 - decimalsDelimeterPosition;
        }
    }
    else {
        result = newValue.toFixed(fraction);
    }
    if (result[0] === '-' && parseFloat(result) === 0) {
        result = result.slice(1);
    }
    let unsignedString = result[0] === '-' ? result.slice(1) : result;
    const firstComma = unsignedString.length - 3 - (fraction > 0 ? 1 + fraction : 0);
    if (firstComma > 0 && Math.abs(newValue) >= 1000) {
        const nChunks = Math.ceil((unsignedString.length - (fraction > 0 ? 1 + fraction : 0)) / 3);
        const chunks = [];
        for (let i = 0, pos = firstComma; i < nChunks; i += 1, pos -= 3) {
            chunks.unshift(unsignedString.substr(pos < 0 ? 0 : pos, pos < 0 ? 3 + pos : 3));
        }
        if (fraction > 0) {
            unsignedString = chunks
                .join(',')
                .concat(unsignedString.slice(-fraction - 1));
        }
        else {
            unsignedString = chunks.join(',');
        }
        result = result[0] === '-' ? `-${unsignedString}` : unsignedString;
    }
    if (currency) {
        const currencySymbol = getSymbolFromCurrency(currency) || currency;
        if (result[0] === '-') {
            result = `-${NON_BREAKING_CHAR}${currencySymbol}${result.slice(1)}`;
        }
        else if (currencySymbol) {
            result = `${currencySymbol}${result}`;
        }
        else {
            result = `${currency}${result}`;
        }
    }
    if (removeZero)
        return result.replace(/(\.\d*[1-9])0+$|\.0+$/, '$1');
    return result;
};
export const formatTryFor0 = (currency, prefix = 'Try') => `${prefix} for ${formatAmount(0, currency, 2)}`;
export const getAdjustedTotalPaid = (totalPaid, refundedAmount) => {
    if (!Number(refundedAmount))
        return totalPaid;
    const total = Number(totalPaid) * 100;
    const refund = Number(refundedAmount) * 100;
    return ((total - refund) / 100).toFixed(2);
};
const formatPositiveAmount = (amount) => {
    'worklet';
    let result = amount.toFixed(2);
    if (result[0] === '-') {
        result = result.slice(1);
    }
    const firstComma = result.length - 6;
    if (firstComma > 0) {
        const nChunks = Math.ceil((result.length - 3) / 3);
        const chunks = [];
        for (let i = 0, pos = firstComma; i < nChunks; i += 1, pos -= 3) {
            chunks.unshift(result.substr(pos < 0 ? 0 : pos, pos < 0 ? 3 + pos : 3));
        }
        result = chunks.join(',').concat(result.slice(-3));
    }
    return result;
};
export const formatAmountWorklet = (amount, currencySymbol, isScrambled = false, scrambleFactor = 1) => {
    'worklet';
    const finalCurrencySymbol = isScrambled ? '¥' : currencySymbol;
    if (amount < 0 && Math.abs(amount * scrambleFactor) >= 0.005) {
        return `-${finalCurrencySymbol}${formatPositiveAmount(Math.abs(amount * scrambleFactor))}`;
    }
    return `${finalCurrencySymbol}${formatPositiveAmount(amount * scrambleFactor)}`;
};
export const formatPhoneNumber = (rawText) => {
    const text = rawText.replace(/[\D]/g, '');
    let phone = text;
    const isSingleDigitCountryCode = text.substr(0, 1) === '1' || text.substr(0, 1) === '7'
        ? text.substr(0, 1)
        : null;
    if (text.length < 2) {
        if (rawText === '+') {
            phone = `+`;
        }
        else {
            phone = `+${text}`;
        }
    }
    else if (text.length < 5) {
        if (isSingleDigitCountryCode) {
            phone = `+${isSingleDigitCountryCode}\xa0${text.substr(1, 3)}`;
        }
        else {
            phone = `+${text.substr(0, 2)}\xa0${text.substr(2, 4)}`;
        }
    }
    else if (text.length < 8) {
        if (isSingleDigitCountryCode) {
            phone = `+${isSingleDigitCountryCode}\xa0${text.substr(1, 3)}\xa0${text.substr(4, 3)}`;
        }
        else if (text.length <= 6) {
            phone = `+${text.substr(0, 2)}\xa0${text.substr(2, 4)}`;
        }
        else {
            // text.length === 7
            phone = `+${text.substr(0, 2)}\xa0${text.substr(2, 4)}\xa0${text.substr(6, 1)}`;
        }
    }
    else if (text.length < 10) {
        if (isSingleDigitCountryCode) {
            phone = `+${isSingleDigitCountryCode}\xa0${text.substr(1, 3)}\xa0${text.substr(4, 3)}\xa0${text.substr(7, 2)}`;
        }
        else {
            phone = `+${text.substr(0, 2)}\xa0${text.substr(2, 4)}\xa0${text.substr(6, 3)}`;
        }
    }
    else if (text.length < 12) {
        if (isSingleDigitCountryCode) {
            phone = `+${isSingleDigitCountryCode}\xa0${text.substr(1, 3)}\xa0${text.substr(4, 3)}\xa0${text.substr(7, 2)}\xa0${text.substr(9, 2)}`;
        }
        else {
            phone = `+${text.substr(0, 2)}\xa0${text.substr(2, 4)}\xa0${text.substr(6, 3)}\xa0${text.substr(9, 2)}`;
        }
    }
    else if (isSingleDigitCountryCode) {
        phone = text;
    }
    else {
        phone = `+${text.substr(0, 2)}\xa0${text.substr(2, 4)}\xa0${text.substr(6, 3)}\xa0${text.substr(9, 3)}`;
    }
    return phone;
};
export const splitAmount = (amount, divider) => {
    if (divider < 2)
        return [amount, 0];
    const inCents = Math.round(amount * 100);
    const remainder = inCents % divider;
    const item = (inCents - remainder) / divider;
    return [(item + remainder) / 100, item / 100];
};
export const DEFAULT_MAX_AMOUNT = 10 ** 9; // for MoneyInput, 1 Billion is not supported by backend
/** Integer digits that keep a typed amount below `DEFAULT_MAX_AMOUNT`. */
export const DEFAULT_MAX_INTEGER_DIGITS = String(DEFAULT_MAX_AMOUNT - 1).length;
export function formatAmountText(text, givenMaxAmount) {
    const maxAmount = givenMaxAmount === undefined
        ? DEFAULT_MAX_AMOUNT
        : givenMaxAmount ?? 10 ** 20; // null effectively disables max amount checks
    let maxLength;
    // let’s ensure text only contains numbers and 1 dot symbol
    let fixedText = text.replace(/,/g, '.').replace(/[^\d.]/g, ''); // only digits and dots
    if (fixedText === '.') {
        // edge case
        fixedText = '0.';
    }
    else if (fixedText === '') {
        fixedText = '0';
    }
    const firstDotIndex = fixedText.indexOf('.');
    if (firstDotIndex !== -1) {
        // there is a dot, make sure it’s the only dot
        const beforeDot = fixedText.substr(0, firstDotIndex);
        const afterDot = fixedText
            .substr(firstDotIndex + 1)
            .replace(/[^\d]/g, '')
            .substr(0, 2); // only 2 digits after dot max
        fixedText = `${beforeDot}.${afterDot}`;
    }
    // now we have fixedText which may come as 01000.
    // to ensure there are no extra leading zeros, we take parseFloat and add a dot if it was last
    if (fixedText.substr(-1) === '.') {
        // there is a dot at the end, so add it back
        fixedText = `${parseFloat(fixedText).toString()}.`;
    }
    else if (fixedText.substr(-2) === '.0') {
        fixedText = `${parseFloat(fixedText).toString()}.0`;
    }
    else if (fixedText.substr(-3) === '.00') {
        fixedText = `${parseFloat(fixedText).toString()}.00`; // keep it with cents
    }
    else if (fixedText.match(/\.\d0$/)) {
        // like 0.60
        fixedText = `${parseFloat(fixedText).toString()}0`; // keep that 0 at the end
    }
    else {
        fixedText = parseFloat(fixedText).toString();
    }
    // amount is just a parsed float of fixedText
    const newAmount = parseFloat(fixedText);
    if (newAmount >= maxAmount) {
        // user entered amount that is greater than maxAmount - shake, vibrate and warn
        return undefined;
    }
    const amount = newAmount;
    const fixedTextResult = fixedText;
    // determine if entering more text can lead to exceeding maxAmount
    if (firstDotIndex !== -1) {
        // if user has started to enter cents, let him finish
        maxLength = firstDotIndex + 3;
    }
    else {
        maxLength = maxAmount.toString().length + 3;
    }
    let pounds = firstDotIndex === -1 ? fixedText : fixedText.substr(0, firstDotIndex);
    // displayAmount only differs by comma symbols
    if (pounds.length > 12) {
        pounds = pounds.replace(/(.*)(\d{3})(\d{3})(\d{3})(\d{3})$/, '$1,$2,$3,$4,$5');
    }
    else if (pounds.length > 9) {
        pounds = pounds.replace(/(.*)(\d{3})(\d{3})(\d{3})$/, '$1,$2,$3,$4');
    }
    else if (pounds.length > 6) {
        pounds = pounds.replace(/(.*)(\d{3})(\d{3})$/, '$1,$2,$3');
    }
    else if (pounds.length > 3) {
        pounds = pounds.replace(/(.*)(\d{3})$/, '$1,$2');
    }
    const displayAmount = firstDotIndex === -1
        ? pounds
        : `${pounds}${fixedText.substr(firstDotIndex)}`;
    return {
        amount,
        fixedText: fixedTextResult,
        displayAmount,
        maxLength,
    };
}
export function getScale(displayAmount) {
    if (displayAmount.length <= 7) {
        return 1;
    }
    return 7.0 / displayAmount.length;
}
export function getInitialScale(maxAmount, initialAmountText) {
    if (!initialAmountText) {
        return 1;
    }
    const values = formatAmountText(initialAmountText, maxAmount);
    if (!values) {
        return 1;
    }
    return getScale(values.displayAmount);
}
export function getNumberOfDaysToDate(date) {
    const days = differenceInCalendarDays(date, new Date());
    switch (true) {
        case days === 1:
            return '1 day';
        case days < 1:
            return '0 days';
        default:
            return `${days} days`;
    }
}
export function isoCodeToEmojiFlag(iso2) {
    // Netherlands Antilles does not have an emoji flag
    if (iso2 === 'AN')
        return flag('NL');
    return flag(iso2);
}
export const formatDateForSectionHeader = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (isSameYear(parsedDate, new Date())) {
        return format(parsedDate, 'MMMM');
    }
    return format(parsedDate, 'MMMM yyyy');
};
export const formatDateForSectionHeaderWithDate = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (isSameYear(parsedDate, new Date())) {
        return format(parsedDate, 'EEEE, d MMMM');
    }
    return format(parsedDate, 'EEEE, d MMMM yyyy');
};
export const createActivitySections = (items, key) => {
    if (!items.length)
        return [];
    if (!items[0][key])
        return [];
    const grouped = [];
    const timestampKey = items[0][key];
    if (items[0]) {
        const parsedDate = parseISO(timestampKey);
        grouped.push({
            title: formatDateForSectionHeader(timestampKey),
            month: getMonth(parsedDate),
            year: getYear(parsedDate),
            data: [{ ...items[0], isFirstInSection: true }],
        });
    }
    for (let i = 1; i < items.length; i++) {
        if (items[i]) {
            const timestampKeyForI = items[i][key];
            const title = formatDateForSectionHeader(timestampKeyForI);
            if (title === grouped[grouped.length - 1].title) {
                grouped[grouped.length - 1].data.push({
                    ...items[i],
                    isFirstInSection: undefined,
                    isLastInSection: undefined,
                });
            }
            else {
                const parsedDate = parseISO(timestampKeyForI);
                grouped.push({
                    title,
                    month: getMonth(parsedDate),
                    year: getYear(parsedDate),
                    data: [{ ...items[i], isFirstInSection: true }],
                });
            }
        }
    }
    grouped.forEach((group) => {
        const itemsLen = group.data.length;
        // eslint-disable-next-line no-param-reassign
        group.data[itemsLen - 1].isLastInSection = true;
    });
    return grouped;
};
const emptyArr = [];
export const createCardViewList = (items) => {
    const itemsLength = items?.length;
    if (itemsLength) {
        // @ts-ignore
        const finalResults = [...items];
        const newFirstItem = {
            ...items[0],
            isFirstItem: true,
        };
        finalResults[0] = newFirstItem;
        const newLastItem = {
            ...finalResults[itemsLength - 1],
            isLastItem: true,
        };
        finalResults[itemsLength - 1] = newLastItem;
        return finalResults;
    }
    return emptyArr;
};
export const removeLineBreaksAndTabs = (title) => title.replace(/(\t\n|\n|\t)/gm, '').trim();
export const formatAddress = ({ streetName, townOrCity, county, buildingName, buildingNumber, subBuildingName, } = {}) => [
    subBuildingName || '',
    buildingName || '',
    streetName
        ? `${!!buildingNumber && buildingNumber.length ? `${buildingNumber} ` : ''}${streetName}`
        : streetName,
    townOrCity,
    county,
]
    .filter(Boolean)
    .join(', ');
export const formatUnsignedInteger = (number) => {
    if (!number) {
        return '0';
    }
    const numberStr = String(number);
    let finalString = '';
    for (let i = 0; i < numberStr.length; i++) {
        if (numberStr.length - i !== numberStr.length &&
            (numberStr.length - i) % 3 === 0) {
            finalString += `,${numberStr[i]}`;
        }
        else {
            finalString += numberStr[i];
        }
    }
    return finalString;
};
export const formatPlural = (singularWord, pluralWord, count) => {
    if (count === 1) {
        return singularWord;
    }
    return pluralWord;
};
/**
 * Formats a single digit number to two
 */
export const formatDoubleDigit = (number) => padStart(number, 2, '0');
export const formatStringList = (array) => {
    if (!array || array.length === 0) {
        return '';
    }
    if (array.length === 1) {
        return array[0];
    }
    if (array.length === 2) {
        return array.join(' and ');
    }
    return array
        .slice(0, array.length - 1)
        .join(', ')
        .concat(', and ')
        .concat(array[array.length - 1]);
};
export const getOrdinalSuffix = (number) => {
    if (number > 3 && number < 21)
        return 'th';
    switch (number % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};
export const capitalizeFirstCharacter = (word) => {
    if (!word)
        return '';
    const firstLetter = word[0].toUpperCase();
    const restOfTheLetters = word.slice(1, word.length).toLowerCase();
    return firstLetter + restOfTheLetters;
};
export const deduplicateWithKey = (array, key) => {
    const reduceObjects = array.reduce((prev, curr) => ({
        ...prev,
        [curr[key]]: curr,
    }), {});
    return Object.values(reduceObjects);
};
export const getTierText = (tier) => {
    switch (tier) {
        case Tier.ultimate:
            return 'Emma Ultimate';
        case Tier.pro:
            return 'Emma Pro';
        case Tier.plus:
            return 'Emma Plus';
        default:
            return 'Basic Plan';
    }
};
/**
 * Calculates the percentage of a part relative to a total and formats it as a string. All trailing 0s are removed.
 *
 * @param {number} part - The part value to calculate the percentage for.
 * @param {number} total - The total value against which the percentage is calculated.
 * @param {number} [fractionDigits=2] - The number of digits to appear after the decimal point. Default is 2.
 * @returns {string} - The formatted percentage string with trailing zeros removed.
 *
 * @example
 * // returns '50%'
 * calculateAndFormatPercentage(1, 2);
 *
 * @example
 * // returns '25%'
 * calculateAndFormatPercentage(50, 200);
 *
 * @example
 * // returns '33.33%'
 * calculateAndFormatPercentage(1, 3);
 *
 * @example
 * // returns '100%'
 * calculateAndFormatPercentage(7, 7, 3);
 *
 * @example
 * // returns '0%'
 * calculateAndFormatPercentage(5, 0);
 */
export const calculateAndFormatPercentage = (part, total, fractionDigits = 2) => {
    if (total === 0) {
        return '0%';
    }
    const percentage = (part / total) * 100;
    const formattedPercentage = percentage
        .toFixed(fractionDigits)
        .replace(/\.?0+$/, '')
        .replace(/(\.\d*[1-9])0+$/, '$1');
    return `${formattedPercentage}%`;
};
export const formatSortCodeAndAccountNumber = (sortCodeStr, accountNumberStr) => {
    const sortCode = sortCodeStr?.replace(/(\d\d)(\d\d)(\d\d)/, '$1-$2-$3');
    let accountNumber = accountNumberStr ? ` ${accountNumberStr}` : '';
    if (!sortCode) {
        accountNumber = accountNumberStr || '';
    }
    return `${sortCode || ''}${accountNumber}`;
};
export const formatSortCode = (sortCode) => {
    if (sortCode) {
        return sortCode?.replace(/(\d\d)(\d\d)(\d\d)/, '$1-$2-$3');
    }
    return '';
};
export const truncateMiddle = (str, maxLength) => {
    if (!str)
        return '';
    if (str.length <= maxLength || maxLength < 5)
        return str;
    const ellipsis = '...';
    const charsToShow = maxLength - ellipsis.length;
    const startChars = Math.ceil(charsToShow / 2);
    const endChars = Math.floor(charsToShow / 2);
    return str.slice(0, startChars) + ellipsis + str.slice(str.length - endChars);
};
export const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
export const getFullName = (info) => (info.firstName || '?') + (info.lastName ? ` ${info.lastName}` : '');
export const formatAmountInput = (text, defaultCurrency) => {
    const formatted = text === '.' ? '0.' : text.replace(/[^0-9.]/g, '');
    const parts = formatted.split('.');
    const wholeNumber = parts[0];
    if (formatted === '') {
        return {
            value: '',
            rawAmount: '',
        };
    }
    const formattedWholeNumber = formatAmount(Number(wholeNumber), defaultCurrency, 0);
    let decimalStr = parts[1] !== undefined ? `.${parts[1]}` : '';
    if (decimalStr) {
        if (decimalStr?.length > 2) {
            decimalStr = `.${parts[1].slice(0, 2)}`;
        }
    }
    const value = formattedWholeNumber + decimalStr;
    return {
        value,
        rawAmount: value.replace(/[^0-9.]/g, ''),
    };
};
export const formatPercentageToMinimum2DecimalPlaces = (num) => {
    const str = num.toString();
    if (str.indexOf('.') === -1) {
        return `${str}.00%`;
    }
    const decimals = str.split('.')[1].length;
    if (decimals === 1) {
        return `${str}0%`;
    }
    return `${str}%`;
};
export const getFractionDigits = (value) => {
    const str = value.toString();
    // Number#toString() switches to exponential notation (e.g. "1e-7") for values
    // with an absolute value below 1e-6, which has no '.' to split on -- parse the
    // mantissa/exponent instead of just counting digits after a decimal point.
    const [mantissa, exponent] = str.toLowerCase().split('e');
    if (exponent === undefined) {
        return mantissa.split('.')[1]?.length ?? 0;
    }
    const mantissaDecimals = mantissa.split('.')[1]?.length ?? 0;
    return Math.max(0, mantissaDecimals - Number(exponent));
};
/** `null` means every decimal the value has, with a minimum of 2 */
export const resolveFractionDigits = (value, fractionDigits) => fractionDigits ?? Math.max(2, getFractionDigits(value));
/** Every decimal of a crypto amount followed by its ticker, e.g. "0.000078 CBETH" */
export const formatCryptoAmount = (amount, currency) => `${formatAmount(amount, undefined, resolveFractionDigits(amount, null))} ${currency}`;
